import { useEffect, useRef } from 'react'
import { useDashboardState } from '@features/dashboard/store/useDashboardState'
import { addDailyActivityEntry } from '@services/firebase/self-study'

export type StudyTimeSurface =
  | 'audio-book'
  | 'flashcards'
  | 'online-ebook'
  | 'pdf-flipbook'
  | 'teaching-presentation'

const STUDY_TIME_IDLE_TIMEOUT_MS = 5 * 60 * 1000
const STUDY_TIME_FLUSH_INTERVAL_SECONDS = 60
const STUDY_TIME_MAX_PENDING_SECONDS = 12 * 60 * 60
const STUDY_TIME_TICK_MS = 1000

const STUDY_TIME_INTERACTION_EVENTS: readonly (keyof WindowEventMap)[] = [
  'keydown',
  'pointerdown',
  'scroll',
  'touchstart',
  'wheel',
]

/**
 * Accumulates "active" study seconds on a learning surface and periodically
 * persists them through `addDailyActivityEntry` (which merges into the daily
 * document and increments the active-day count once per day).
 *
 * Active means: the tab is visible AND (the learner interacted within the
 * idle timeout OR `isPlaying` is true for media surfaces such as audiobooks).
 *
 * Flushes happen every 60 accumulated seconds, when the tab becomes hidden,
 * and on unmount. Writes are best-effort: failures are re-queued once (bounded)
 * and never block the learning experience.
 */
export function useStudyTimeTracker({
  enabled = true,
  isPlaying = false,
  surface,
}: {
  readonly enabled?: boolean
  readonly isPlaying?: boolean
  readonly surface: StudyTimeSurface
}) {
  const { actions, state } = useDashboardState()
  const pendingSecondsRef = useRef(0)
  const lastInteractionAtRef = useRef(0)
  const isFlushingRef = useRef(false)
  const latestRef = useRef({
    actions,
    dailyActivities: state.myUser?.daily_activities,
    enabled,
    hasUser: Boolean(state.myUser),
    isPlaying,
  })

  useEffect(() => {
    latestRef.current = {
      actions,
      dailyActivities: state.myUser?.daily_activities,
      enabled,
      hasUser: Boolean(state.myUser),
      isPlaying,
    }
  })

  useEffect(() => {
    if (!enabled) {
      return
    }

    lastInteractionAtRef.current = Date.now()

    const handleInteraction = () => {
      lastInteractionAtRef.current = Date.now()
    }

    const flush = async () => {
      const pendingSeconds = Math.floor(pendingSecondsRef.current)

      if (pendingSeconds < 1 || isFlushingRef.current || !latestRef.current.hasUser) {
        return
      }

      isFlushingRef.current = true
      pendingSecondsRef.current -= pendingSeconds

      try {
        const activity = await addDailyActivityEntry({ seconds: pendingSeconds })
        const {
          actions: latestActions,
          dailyActivities,
        } = latestRef.current

        latestActions.mergeMyUser({
          daily_activities: (() => {
            const existingActivities = dailyActivities ?? []
            const existingIndex = existingActivities.findIndex(
              (entry) => entry.id === activity.id,
            )

            if (existingIndex >= 0) {
              const nextActivities = [...existingActivities]
              nextActivities[existingIndex] = activity
              return nextActivities
            }

            return [...existingActivities, activity]
          })(),
        })
      } catch (error) {
        pendingSecondsRef.current = Math.min(
          pendingSecondsRef.current + pendingSeconds,
          STUDY_TIME_MAX_PENDING_SECONDS,
        )
        console.warn(`[study-time-tracker] flush failed for surface "${surface}"`, error)
      } finally {
        isFlushingRef.current = false
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void flush()
      }
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || !latestRef.current.enabled) {
        return
      }

      const idleForMs = Date.now() - lastInteractionAtRef.current

      if (!latestRef.current.isPlaying && idleForMs > STUDY_TIME_IDLE_TIMEOUT_MS) {
        return
      }

      pendingSecondsRef.current += STUDY_TIME_TICK_MS / 1000

      if (Math.floor(pendingSecondsRef.current) >= STUDY_TIME_FLUSH_INTERVAL_SECONDS) {
        void flush()
      }
    }, STUDY_TIME_TICK_MS)

    STUDY_TIME_INTERACTION_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleInteraction, { passive: true })
    })
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      STUDY_TIME_INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleInteraction)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      void flush()
    }
  }, [enabled, surface])
}
